import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { spreadsheetArticle, articlesList } from '@/mocks/articles';
import { mainSiteUrl } from '@/mocks/categories';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function ArticlePage() {
  const { t, i18n } = useTranslation();
  const { lang = 'en', slug } = useParams<{ lang: string; slug: string }>();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('');
  const [animatedSections, setAnimatedSections] = useState<Set<string>>(new Set());

  const article = spreadsheetArticle;

  useEffect(() => {
    i18n.changeLanguage(lang);
  }, [lang, i18n]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAnimatedSections((prev) => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll('[data-animate]').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const sections = article.sections.map((s) => s.id);
      for (let i = sections.length - 1; i >= 0; i--) {
        const el = document.getElementById(sections[i]);
        if (el && el.getBoundingClientRect().top < 200) {
          setActiveSection(sections[i]);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [article.sections]);

  const getFadeClass = (id: string) =>
    animatedSections.has(id)
      ? 'opacity-100 translate-y-0'
      : 'opacity-0 translate-y-6';

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const relatedArticles = articlesList
    .filter((a) => article.relatedArticles.includes(a.slug))
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Breadcrumb */}
      <div className="pt-20 md:pt-24 pb-4 bg-white border-b border-background-200/70">
        <div className="container-main">
          <nav className="flex items-center gap-2 text-sm text-foreground-500">
            <a href={`/${lang}/`} className="hover:text-primary-600 transition-colors">
              {t('breadcrumb.home')}
            </a>
            <i className="ri-arrow-right-s-line text-foreground-300"></i>
            <span className="text-foreground-700">{article.title}</span>
          </nav>
        </div>
      </div>

      {/* Article Header */}
      <header className="pt-10 pb-12 bg-white">
        <div className="container-main max-w-4xl">
          <div className="mb-6">
            <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full uppercase tracking-wide">
              Guide
            </span>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-6 leading-tight">
            {article.h1}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line"></i>
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              {t('article.updated')}: {article.updatedDate}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-time-line"></i>
              {article.readTime} {t('article.readTime')}
            </span>
          </div>
        </div>
      </header>

      <div className="container-main max-w-6xl py-12">
        <div className="grid lg:grid-cols-[1fr_280px] gap-10">
          {/* Main Content */}
          <div className="order-2 lg:order-1">
            {/* Table of Contents */}
            <div id="toc" data-animate className={`mb-12 transition-all duration-700 ${getFadeClass('toc')}`}>
              <div className="bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70">
                <h2 className="font-heading text-lg font-bold text-foreground-900 mb-4 flex items-center gap-2">
                  <i className="ri-list-check text-primary-500"></i>
                  {t('article.toc')}
                </h2>
                <ul className="space-y-2">
                  {article.sections.map((section) => (
                    <li key={section.id}>
                      <button
                        onClick={() => scrollToSection(section.id)}
                        className={`text-left text-sm transition-colors hover:text-primary-600 ${
                          activeSection === section.id ? 'text-primary-600 font-medium' : 'text-foreground-600'
                        }`}
                      >
                        {section.h2}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Article Sections */}
            {article.sections.map((section, index) => (
              <section
                key={section.id}
                id={section.id}
                data-animate
                className={`mb-12 transition-all duration-700 ${getFadeClass(section.id)}`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-5">
                  {section.h2}
                </h2>
                {section.content.map((paragraph, pIndex) => (
                  <p key={pIndex} className="text-foreground-700 leading-relaxed mb-4">
                    {paragraph}
                  </p>
                ))}

                {/* Comparison Table */}
                {section.isTable && section.tableData && (
                  <div className="overflow-x-auto my-6">
                    <table className="w-full border-collapse bg-white rounded-lg border border-background-200/70 overflow-hidden">
                      <thead>
                        <tr className="bg-background-100">
                          {section.tableData.headers.map((header, hIndex) => (
                            <th
                              key={hIndex}
                              className="px-4 py-3 text-left text-sm font-semibold text-foreground-800 border-b border-background-200/70"
                            >
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {section.tableData.rows.map((row, rIndex) => (
                          <tr key={rIndex} className="hover:bg-background-50/50 transition-colors">
                            {row.map((cell, cIndex) => (
                              <td
                                key={cIndex}
                                className="px-4 py-3 text-sm text-foreground-700 border-b border-background-200/50"
                              >
                                {cell}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            ))}

            {/* FAQ Section */}
            <section id="faq" data-animate className={`mb-12 transition-all duration-700 ${getFadeClass('faq')}`}>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
                {t('faq.title')}
              </h2>
              <div className="space-y-4">
                {article.faq.map((item, index) => (
                  <div key={index} className="card p-6">
                    <h3 className="font-heading font-semibold text-foreground-900 mb-3 flex items-start gap-2">
                      <i className="ri-question-line text-primary-500 mt-1 flex-shrink-0"></i>
                      {item.question}
                    </h3>
                    <p className="text-sm text-foreground-600 leading-relaxed pl-7">
                      {item.answer}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            {/* CTA Section */}
            <div id="cta" data-animate className={`mb-12 transition-all duration-700 ${getFadeClass('cta')}`}>
              <div className="bg-primary-50 rounded-xl p-8 md:p-10 border border-primary-200/50">
                <h3 className="font-heading text-xl font-bold text-foreground-900 mb-3">
                  {t('finalCta.title')}
                </h3>
                <p className="text-foreground-600 mb-6">
                  {t('finalCta.subtitle')}
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href={mainSiteUrl}
                    target="_blank"
                    rel="noopener nofollow"
                    className="btn-primary"
                  >
                    {t('finalCta.button')}
                    <i className="ri-arrow-right-up-line ml-2"></i>
                  </a>
                  <a
                    href={`/${lang}/`}
                    className="btn-outline"
                  >
                    {t('article.backToHome')}
                    <i className="ri-arrow-left-line ml-2"></i>
                  </a>
                </div>
              </div>
            </div>

          </div>

          {/* Sidebar */}
          <aside className="order-1 lg:order-2">
            <div className="lg:sticky lg:top-28 space-y-6">
              {/* Quick Navigation */}
              <div className="bg-white rounded-xl p-5 border border-background-200/70">
                <h3 className="font-heading font-semibold text-foreground-900 mb-4 text-sm">
                  {t('article.toc')}
                </h3>
                <ul className="space-y-2">
                  {article.sections.map((section) => (
                    <li key={section.id}>
                      <button
                        onClick={() => scrollToSection(section.id)}
                        className={`text-left text-xs transition-colors hover:text-primary-600 ${
                          activeSection === section.id ? 'text-primary-600 font-medium' : 'text-foreground-500'
                        }`}
                      >
                        {section.h2}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Related Articles */}
              <div className="bg-white rounded-xl p-5 border border-background-200/70">
                <h3 className="font-heading font-semibold text-foreground-900 mb-4 text-sm">
                  {t('article.related')}
                </h3>
                <div className="space-y-3">
                  {relatedArticles.map((related) => (
                    <a
                      key={related.id}
                      href={`/${lang}/${related.slug}/`}
                      className="block p-3 rounded-lg bg-background-50 hover:bg-primary-50 transition-colors group"
                    >
                      <h4 className="text-sm font-medium text-foreground-800 group-hover:text-primary-700 transition-colors mb-1">
                        {related.title}
                      </h4>
                      <p className="text-xs text-foreground-500 line-clamp-2">
                        {related.description}
                      </p>
                    </a>
                  ))}
                </div>
              </div>

              {/* Main Site CTA */}
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="block bg-accent-500 rounded-xl p-5 text-white hover:bg-accent-600 transition-colors"
              >
                <h3 className="font-heading font-semibold mb-2 text-sm">
                  {t('nav.mainSite')}
                </h3>
                <p className="text-xs text-white/80 mb-3">
                  Visit kaspreadsheet.com to explore products and start shopping.
                </p>
                <span className="inline-flex items-center text-xs font-medium">
                  Go to Main Site
                  <i className="ri-arrow-right-up-line ml-1"></i>
                </span>
              </a>
            </div>
          </aside>
        </div>
      </div>

      <Footer />
    </div>
  );
}